import React from 'react';
import ToolTip from '../help/ToolTip';
import { useSelector } from 'react-redux';
import { isPremiumOrPro } from '../../utils/SubUtils';
import LockedFeature from './LockedFeature';

const AnalystRatings = ({ data }) => {
    const user = useSelector((state) => state.auth.user);
    const subscription = user?.user_subscription;
    const isPro = isPremiumOrPro(subscription);

    if (!isPro) {
        return (
            <LockedFeature 
                title="Analyst Ratings Locked" 
                description={<span>Upgrade to <strong>Premium or Pro</strong> plan to unlock detailed analyst ratings and target prices.</span>} 
            />
        );
    }

    if (!data) return <div className="p-5 text-center">Loading data...</div>;

  const {
    recommendation_mean_label,
    recommendation_number_of_total,
    recommendation_number_of_buy,
    recommendation_number_of_hold,
    recommendation_number_of_sell,
    recommendation_number_of_strong_buy,
    recommendation_number_of_strong_sell,
    price_target,
    historical_snapshots,
    current_price
  } = data;

  const calculatePercent = (count, total) => {
      if(!total || total === 0) return 0;
      return Math.round((count / total) * 100);
  }

  const ratings = [
    { label: 'Strong Buy', count: recommendation_number_of_strong_buy, percent: calculatePercent(recommendation_number_of_strong_buy, recommendation_number_of_total), color: '#52c41a' },
    { label: 'Buy', count: recommendation_number_of_buy, percent: calculatePercent(recommendation_number_of_buy, recommendation_number_of_total), color: '#a0d911' },
    { label: 'Hold', count: recommendation_number_of_hold, percent: calculatePercent(recommendation_number_of_hold, recommendation_number_of_total), color: '#fadb14' },
    { label: 'Sell', count: recommendation_number_of_sell, percent: calculatePercent(recommendation_number_of_sell, recommendation_number_of_total), color: '#fa8c16' },
    { label: 'Strong Sell', count: recommendation_number_of_strong_sell, percent: calculatePercent(recommendation_number_of_strong_sell, recommendation_number_of_total), color: '#f5222d' },
  ];

  // Map historical snapshots to trends
  // The API provides: "1M Ago", "3M Ago", "6M Ago", "12M Ago"
  const trendLabels = ['12M Ago', '6M Ago', '3M Ago', 'Current'];
  const trends = trendLabels.map(label => {
      const snapshot = historical_snapshots ? historical_snapshots[label] : null;
      if (!snapshot) return { label, values: [0, 0, 0, 0, 0], counts: { strong_buy: 0, buy: 0, hold: 0, sell: 0, strong_sell: 0 } };
      // Order: Strong Buy, Buy, Hold, Sell, Strong Sell (matching colors)
      return {
          label,
          values: [
              snapshot.strong_buy_percentage,
              snapshot.buy_percentage,
              snapshot.hold_percentage,
              snapshot.sell_percentage,
              snapshot.strong_sell_percentage
          ],
          counts: {
              strong_buy: snapshot.strong_buy,
              buy: snapshot.buy,
              hold: snapshot.hold,
              sell: snapshot.sell,
              strong_sell: snapshot.strong_sell
          }
      };
  });

  const trendColors = ['#52c41a', '#a0d911', '#fadb14', '#fa8c16', '#f5222d'];

  return (
    <div className="stock-dashboard-wrapper mt-4">
      {/* Top Cards Row */}
      <div className="row g-4 mb-4">
        <div className="col-lg-3">
          <div className="custom-card border-0 shadow-sm rounded-4 p-4 text-center h-100 bg-success-subtle bg-opacity-10">
            <p className="text-secondary small mb-2">Consensus Rating</p>
            <h1 className="display-4 text-success fw-normal mb-1">{recommendation_mean_label || '-'}</h1>
            <p className="text-muted small mb-0">{recommendation_number_of_total} Analysts</p>
          </div>
        </div>
        {[
          { label: 'Price Target', val: price_target ? `$${price_target}` : '-', sub: 'Median Target' },
          { label: 'Upside', val: (price_target && current_price) ? `${((price_target - current_price) / current_price * 100).toFixed(2)}%` : '-', sub: 'From current price' },
          { label: 'Yesterday Closed Price', val: current_price ? `$${current_price}` : '-', sub: '' },
        ].map((item, i) => (
          <div className="col-lg-3" key={i}>
            <div className="custom-card border-0 shadow-sm rounded-4 p-4 h-100">
              <div className="d-flex justify-content-between">
                <p className="text-secondary small">{item.label}</p>
                <i className="bi bi-bullseye text-primary opacity-50"></i>
              </div>
              <h3 className="h4 mb-1 fw-normal">{item.val}</h3>
              <p className="text-muted extra-small mb-0">{item.sub}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Charts Row */}
      <div className="row g-4">
        {/* Current Ratings Distribution */}
        <div className="col-lg-6">
          <div className="custom-card border-0 shadow-sm rounded-4 p-4">
            <h5 className="mb-4 fw-normal">Current Ratings Distribution</h5>
            {ratings.map((r, i) => (
              <div key={i} className="mb-3">
                <div className="d-flex justify-content-between small mb-1">
                  <span>{r.label}</span>
                  <span className="fw-medium">{r.count} ({r.percent}%)</span>
                </div>
                <div className="progress rounded-pill" style={{ height: '6px' }}>
                  <div 
                    className="progress-bar ratings" 
                    style={{ width: `${r.percent}%`,'--ratings-color': r.color }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Rating Trends Stacked Bars */}
        <div className="col-lg-6">
          <div className="custom-card border-0 shadow-sm rounded-4 p-4">
            <h5 className="mb-4 fw-normal">Rating Trends</h5>
            <div className="d-flex justify-content-between align-items-end mb-4" style={{ height: '200px' }}>
              {trends.map((t, i) => {
                  const tooltipText = [
                    t.counts.strong_sell > 0 ? `${t.counts.strong_sell} Strong Sell` : '',
                    t.counts.sell > 0 ? `${t.counts.sell} Sell` : '',
                    t.counts.hold > 0 ? `${t.counts.hold} Hold` : '',
                    t.counts.buy > 0 ? `${t.counts.buy} Buy` : '',
                    t.counts.strong_buy > 0 ? `${t.counts.strong_buy} Strong Buy` : '',
                  ].filter(Boolean).join(', ');

                  return (
                    <div key={i} className="text-center" style={{ width: '20%' }}>
                      <ToolTip content={tooltipText || 'No ratings'}>
                      <div className="d-flex flex-column-reverse rounded-3 overflow-hidden" style={{ height: '180px' }}>
                        {t.values.map((val, idx) => (
                          val > 0 && <div 
                            key={idx} 
                            style={{ height: `${val}%`, backgroundColor: trendColors[idx] }}
                            className="w-100"
                          ></div>
                        ))}
                      </div>
                      </ToolTip>
                      <span className="extra-small text-muted mt-2 d-block">{t.label}</span>
                    </div>
                  );
              })}
            </div>
            {/* Legend */}
            <div className="d-flex flex-wrap justify-content-center gap-3 border-top pt-3">
              {ratings.map((r, i) => (
                <div key={i} className="d-flex align-items-center extra-small text-muted">
                  <div className="rounded-circle me-1" style={{ width: '8px', height: '8px', backgroundColor: r.color }}></div>
                  {r.label}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalystRatings;